import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type UserRole = 'admin' | 'manager' | 'user';

interface UserContextType {
  userRole: UserRole;
  setUserRole: (role: UserRole) => void;
  canDeleteAll: boolean;
  canManageUsers: boolean;
  canViewAnalytics: boolean;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

const USER_ROLE_STORAGE_KEY = 'user-role';

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [userRole, setUserRole] = useState<UserRole>('user'); // Default to user for security

  useEffect(() => {
    // Load user role from localStorage on app start
    const savedRole = localStorage.getItem(USER_ROLE_STORAGE_KEY);
    if (savedRole && ['admin', 'manager', 'user'].includes(savedRole)) {
      setUserRole(savedRole as UserRole);
    }
  }, []);

  const handleSetUserRole = (role: UserRole) => {
    setUserRole(role);
    localStorage.setItem(USER_ROLE_STORAGE_KEY, role);
  };

  // Permission checks based on role
  const canDeleteAll = userRole === 'admin' || userRole === 'manager';
  const canManageUsers = userRole === 'admin';
  const canViewAnalytics = userRole === 'admin' || userRole === 'manager';

  const value: UserContextType = {
    userRole,
    setUserRole: handleSetUserRole,
    canDeleteAll,
    canManageUsers,
    canViewAnalytics,
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
